/**
 * @file td-mobile-menu-handler.js
 * @description Handles all mobile navigation menu functionality
 * @version 1.0.0
 */

if (!customElements.get("td-mobile-navigation")) {
    class TDMobileNavigation extends HTMLElement {
        constructor() {
            super();

            this.ANIMATION_DURATION = 300;
            this.FOCUSABLE_SELECTORS = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
            this.FOCUS_DELAY = 50;

            this.state = {
                isMobileMenuOpen: false
            };

            this._boundHandlers = {
                windowScroll: null,
                windowResize: null,
                documentKeydown: null,
                containerKeydown: null,
                elementHandlers: new Map()
            };

            this._closeTimeout = null;
            this._resizeTimeout = null;
        }

        connectedCallback() {
            this.mobileElements = {
                mobileMenuContainer: document.querySelector('.td-mobile-menu'),
                mobileMenuToggle: document.querySelector('.js-mobile-menu-toggle'),
                mobileMenuClose: document.querySelector('.js-td-mobile-menu-close'),
                submenuTriggers: document.querySelectorAll('.td-mobile-menu__submenu-trigger'),
                backButtons: document.querySelectorAll('.td-mobile-menu__back-button')
            };

            this.SELECTORS = {
                grandchildPanels: '.td-mobile-menu__grandchild-panel',
                submenuPanels: '.td-mobile-menu__submenu-panel',
                activePanels: '.td-mobile-menu__panel.is-active',
                expandedTriggers: '[data-submenu-id][aria-expanded="true"], [data-grandchild-id][aria-expanded="true"]'
            };

            this.setupMobileMenu();
            this.setupEventListeners();
            this.setupScrollPositioning();
            this.updateFocusability();
            this.setupResizeListener();
        }

        disconnectedCallback() {
            this.cleanupEventListeners();
            
            // Clear any pending close timeout
            if (this._closeTimeout) {
                clearTimeout(this._closeTimeout);
                this._closeTimeout = null;
            }
            
            // Clear any pending resize timeout
            if (this._resizeTimeout) {
                clearTimeout(this._resizeTimeout);
                this._resizeTimeout = null;
            }
            
            // Reset body overflow state to prevent page from being unscrollable
            // if the component disconnects while the menu is open
            if (this.state.isMobileMenuOpen) {
                document.body.classList.remove('td-hide-overflow');
            }
            
            // Reset menu container state if it exists
            if (this.mobileElements?.mobileMenuContainer) {
                this.mobileElements.mobileMenuContainer.classList.remove('is-active', 'is-closing');
            }
            
            // Reset toggle button state if it exists
            if (this.mobileElements?.mobileMenuToggle) {
                this.mobileElements.mobileMenuToggle.classList.remove('is-active');
                this.mobileElements.mobileMenuToggle.setAttribute('aria-expanded', 'false');
            }
            
            // Reset internal state
            this.state.isMobileMenuOpen = false;
        }

        cleanupEventListeners() {
            if (this._boundHandlers.windowScroll) {
                window.removeEventListener('scroll', this._boundHandlers.windowScroll, { passive: true });
                this._boundHandlers.windowScroll = null;
            }

            if (this._boundHandlers.windowResize) {
                window.removeEventListener('resize', this._boundHandlers.windowResize, { passive: true });
                this._boundHandlers.windowResize = null;
            }

            if (this._boundHandlers.documentKeydown) {
                document.removeEventListener('keydown', this._boundHandlers.documentKeydown);
                this._boundHandlers.documentKeydown = null;
            }

            if (this._boundHandlers.containerKeydown && this.mobileElements?.mobileMenuContainer) {
                this.mobileElements.mobileMenuContainer.removeEventListener('keydown', this._boundHandlers.containerKeydown);
                this._boundHandlers.containerKeydown = null;
            }

            this._boundHandlers.elementHandlers.forEach((handlers, element) => {
                handlers.forEach(({ type, handler, options }) => {
                    element.removeEventListener(type, handler, options);
                });
            });
            this._boundHandlers.elementHandlers.clear();
        }

        setupEventListeners() {
            this.cleanupEventListeners();

            if (!this.mobileElements.mobileMenuContainer || !this.mobileElements.mobileMenuToggle) return;

            const toggleHandler = () => this.toggleMobileMenu();
            this.mobileElements.mobileMenuToggle.addEventListener('click', toggleHandler);
            this._storeElementHandler(this.mobileElements.mobileMenuToggle, 'click', toggleHandler);

            if (this.mobileElements.mobileMenuClose) {
                const closeHandler = () => this.closeMobileMenu();
                this.mobileElements.mobileMenuClose.addEventListener('click', closeHandler);
                this._storeElementHandler(this.mobileElements.mobileMenuClose, 'click', closeHandler);
            }

            if (this.mobileElements.submenuTriggers?.length > 0) {
                this.mobileElements.submenuTriggers.forEach(trigger => {
                    const clickHandler = (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.openSubmenu(trigger);
                    };
                    trigger.addEventListener('click', clickHandler);
                    this._storeElementHandler(trigger, 'click', clickHandler);
                    this.addKeyboardSupport(trigger, () => this.openSubmenu(trigger));
                });
            }

            const grandchildTriggers = document.querySelectorAll('.td-mobile-menu__grandchild-trigger');
            if (grandchildTriggers.length > 0) {
                grandchildTriggers.forEach(trigger => {
                    const clickHandler = (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.openGrandchildPanel(trigger);
                    };
                    trigger.addEventListener('click', clickHandler);
                    this._storeElementHandler(trigger, 'click', clickHandler);
                    this.addKeyboardSupport(trigger, () => this.openGrandchildPanel(trigger));
                });
            }

            if (this.mobileElements.backButtons?.length > 0) {
                this.mobileElements.backButtons.forEach(button => {
                    const clickHandler = () => this.handleBackButton(button);
                    button.addEventListener('click', clickHandler);
                    this._storeElementHandler(button, 'click', clickHandler);
                    this.addKeyboardSupport(button, () => this.handleBackButton(button));
                });
            }

            this._boundHandlers.documentKeydown = (e) => {
                if (e.key === 'Escape' && this.state.isMobileMenuOpen && this.isMobileViewport()) {
                    this.closeMobileMenu();
                }
            };
            document.addEventListener('keydown', this._boundHandlers.documentKeydown);

            this.setupFocusTrap(this.mobileElements.mobileMenuContainer);
        }

        _storeElementHandler(element, type, handler, options = false) {
            if (!this._boundHandlers.elementHandlers.has(element)) {
                this._boundHandlers.elementHandlers.set(element, []);
            }
            this._boundHandlers.elementHandlers.get(element).push({ type, handler, options });
        }

        addKeyboardSupport(element, handler) {
            const keydownHandler = (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handler();
                }
            };
            element.addEventListener('keydown', keydownHandler);
            this._storeElementHandler(element, 'keydown', keydownHandler);
        }
        
        focusFirstElement(container, delay = this.FOCUS_DELAY) {
            const firstFocusable = container.querySelector(this.FOCUSABLE_SELECTORS);
            if (firstFocusable) setTimeout(() => firstFocusable.focus(), delay);
        }

        blurPanelFocus(panel) {
            const focusedElement = panel.querySelector(':focus');
            if (focusedElement) focusedElement.blur();
        }

        closePanel(panel) {
            if (!panel) return;

            this.blurPanelFocus(panel);

            const isGrandchild = panel.classList.contains('td-mobile-menu__grandchild-panel');

            if (isGrandchild) {
                const parentPanelId = panel.getAttribute('data-parent-panel');
                if (parentPanelId) {
                    const parentPanel = document.getElementById(parentPanelId);
                    if (parentPanel) parentPanel.classList.remove('has-active-grandchild');
                }
            }

            panel.classList.remove('is-active', 'has-active-submenu', 'has-active-grandchild');
            panel.setAttribute('aria-hidden', 'true');

            const panelId = panel.id;
            const selector = isGrandchild
                ? `[data-grandchild-id="${panelId}"]`
                : `[data-submenu-id="${panelId}"]`;
            const trigger = document.querySelector(selector);
            if (trigger) trigger.setAttribute('aria-expanded', 'false');

            return trigger;
        }

        closeAllPanels(excludePanelId = null) {
            const activePanels = document.querySelectorAll(this.SELECTORS.activePanels);

            activePanels.forEach(panel => {
                if (excludePanelId && panel.id === excludePanelId) return;
                this.closePanel(panel);
            });

            const submenuPanels = document.querySelectorAll(this.SELECTORS.submenuPanels);
            submenuPanels.forEach(panel => {
                if (!excludePanelId || panel.id !== excludePanelId) panel.classList.remove('has-active-grandchild');
            });

            if (!excludePanelId) this.mainPanel?.classList.remove('has-active-submenu');
        }

        resetAllPanels() {
            this.closeAllPanels();

            const expandedTriggers = document.querySelectorAll(this.SELECTORS.expandedTriggers);
            expandedTriggers.forEach(trigger => trigger.setAttribute('aria-expanded', 'false'));
        }

        setupMobileMenu() {
            try {
                if (!this.mobileElements.mobileMenuContainer || !this.mobileElements.mobileMenuToggle) {
                    console.warn('TD Navigation: Mobile menu elements not found');
                    return;
                }

                this.mainPanel = document.querySelector('.td-mobile-menu__main-panel');
                if (!this.mainPanel) {
                    console.warn('TD Navigation: Main panel not found');
                    return;
                }
            } catch (error) {
                console.error('TD Navigation: Error initializing mobile menu', error);
            }
        }

        setupScrollPositioning() {
            this.mobileHeader = document.querySelector('.td-mobile-header');

            this.updateMenuPosition();

            let ticking = false;
            this._boundHandlers.windowScroll = () => {
                if (!ticking) {
                    window.requestAnimationFrame(() => {
                        this.updateMenuPosition();
                        ticking = false;
                    });
                    ticking = true;
                }
            };
            window.addEventListener('scroll', this._boundHandlers.windowScroll, { passive: true });

        }

        updateMenuPosition() {
            if (!this.mobileElements.mobileMenuContainer || !this.mobileHeader) return;
            
            void this.mobileHeader.offsetHeight;

            const headerRect = this.mobileHeader.getBoundingClientRect();
            const topPosition = Math.round(headerRect.bottom);

            this.mobileElements.mobileMenuContainer.style.top = `${topPosition}px`;
            const availableHeight = Math.round(window.innerHeight - topPosition);
            this.mobileElements.mobileMenuContainer.style.height = `${availableHeight}px`;
        }

        isMobileViewport() {
            return window.innerWidth < 1000;
        }

        updateFocusability() {
            if (!this.mobileElements.mobileMenuContainer) return;

            const isMobile = this.isMobileViewport();
            const focusableElements = this.mobileElements.mobileMenuContainer.querySelectorAll(this.FOCUSABLE_SELECTORS);

            focusableElements.forEach(element => {
                if (isMobile) {
                    if (element.hasAttribute('data-original-tabindex')) {
                        const originalTabindex = element.getAttribute('data-original-tabindex');
                        if (originalTabindex === '' || originalTabindex === null) {
                            element.removeAttribute('tabindex');
                        } else {
                            element.setAttribute('tabindex', originalTabindex);
                        }
                        element.removeAttribute('data-original-tabindex');
                    }
                } else {
                    if (!element.hasAttribute('data-original-tabindex')) {
                        const currentTabindex = element.getAttribute('tabindex');
                        if (currentTabindex !== null) {
                            element.setAttribute('data-original-tabindex', currentTabindex);
                        } else {
                            element.setAttribute('data-original-tabindex', '');
                        }
                        element.setAttribute('tabindex', '-1');
                    }
                }
            });
        }

        setupResizeListener() {
            // Clear any existing resize timeout
            if (this._resizeTimeout) {
                clearTimeout(this._resizeTimeout);
                this._resizeTimeout = null;
            }
            
            this._boundHandlers.windowResize = () => {
                // Update menu position immediately
                this.updateMenuPosition();
                
                // Debounce focusability update
                if (this._resizeTimeout) {
                    clearTimeout(this._resizeTimeout);
                }
                this._resizeTimeout = setTimeout(() => {
                    this.updateFocusability();
                    this._resizeTimeout = null;
                }, 100);
            };
            window.addEventListener('resize', this._boundHandlers.windowResize, { passive: true });
        }

        setupFocusTrap(container) {
            if (!container) return;

            this._boundHandlers.containerKeydown = (e) => {
                if (!this.isMobileViewport() || !this.state.isMobileMenuOpen || e.key !== 'Tab') return;

                const focusableElements = Array.from(container.querySelectorAll(this.FOCUSABLE_SELECTORS))
                    .filter(el => {
                        const style = window.getComputedStyle(el);
                        if (style.display === 'none' || style.visibility === 'hidden') return false;

                        const panel = el.closest('.td-mobile-menu__panel');
                        if (!panel) return false;

                        if (panel.classList.contains('td-mobile-menu__main-panel')) {
                            return !panel.classList.contains('has-active-submenu');
                        }

                        if (panel.classList.contains('td-mobile-menu__submenu-panel')) {
                            return panel.classList.contains('is-active') && !panel.classList.contains('has-active-grandchild');
                        }

                        return panel.classList.contains('is-active');
                    });

                if (focusableElements.length === 0) return;

                const firstFocusable = focusableElements[0];
                const lastFocusable = focusableElements[focusableElements.length - 1];

                if (e.shiftKey && document.activeElement === firstFocusable) {
                    e.preventDefault();
                    lastFocusable.focus();
                } else if (!e.shiftKey && document.activeElement === lastFocusable) {
                    e.preventDefault();
                    firstFocusable.focus();
                }
            };
            container.addEventListener('keydown', this._boundHandlers.containerKeydown);
        }

        toggleMobileMenu() {
            try {
                if (!this.isMobileViewport()) return;

                const isClosing = this.mobileElements.mobileMenuContainer.classList.contains('is-closing');
                const isActive = this.mobileElements.mobileMenuContainer.classList.contains('is-active');
                const isOpening = !isActive && !isClosing;

                if (isClosing) {
                    // Cancel closing animation and open immediately
                    if (this._closeTimeout) {
                        clearTimeout(this._closeTimeout);
                        this._closeTimeout = null;
                    }
                    this.mobileElements.mobileMenuContainer.classList.remove('is-closing');
                    this.mobileElements.mobileMenuContainer.classList.add('is-active');
                    this.mobileElements.mobileMenuToggle.classList.add('is-active');
                    this.mobileElements.mobileMenuToggle.setAttribute('aria-expanded', 'true');
                    this.toggleBodyOverflow(true);
                    this.state.isMobileMenuOpen = true;
                    this.updateMenuPosition();
                    this.updateFocusability();
                    this.focusFirstElement(this.mobileElements.mobileMenuContainer, this.ANIMATION_DURATION);
                    return;
                }

                if (isOpening) {
                    this.updateMenuPosition();
                    this.updateFocusability();
                }

                this.mobileElements.mobileMenuContainer.classList.toggle('is-active');
                this.mobileElements.mobileMenuToggle.classList.toggle('is-active');
                const isNowActive = this.mobileElements.mobileMenuContainer.classList.contains('is-active');
                this.mobileElements.mobileMenuToggle.setAttribute('aria-expanded', isNowActive ? 'true' : 'false');

                this.toggleBodyOverflow(isNowActive);
                this.state.isMobileMenuOpen = isNowActive;

                if (isOpening) {
                    this.focusFirstElement(this.mobileElements.mobileMenuContainer, this.ANIMATION_DURATION);
                } else {
                    this.mobileElements.mobileMenuToggle?.focus();
                    this.resetAllPanels();
                }
            } catch (error) {
                console.error('TD Navigation: Error toggling mobile menu', error);
            }
        }

        closeMobileMenu() {
            try {
                // Clear any existing close timeout
                if (this._closeTimeout) {
                    clearTimeout(this._closeTimeout);
                }

                this.mobileElements.mobileMenuContainer.classList.add('is-closing');
                this.mobileElements.mobileMenuToggle.classList.remove('is-active');
                this.mobileElements.mobileMenuToggle.setAttribute('aria-expanded', 'false');

                this._closeTimeout = setTimeout(() => {
                    this.mobileElements.mobileMenuContainer.classList.remove('is-active', 'is-closing');
                    this.toggleBodyOverflow(false);
                    this.state.isMobileMenuOpen = false;

                    this.resetAllPanels();
                    this.mobileElements.mobileMenuToggle?.focus();
                    this._closeTimeout = null;
                }, this.ANIMATION_DURATION);
            } catch (error) {
                console.error('TD Navigation: Error closing mobile menu', error);
            }
        }

        openSubmenu(trigger) {
            try {
                const submenuId = trigger.getAttribute('data-submenu-id');
                if (!submenuId) return;

                const submenuPanel = document.getElementById(submenuId);
                if (!submenuPanel) {
                    console.warn('TD Navigation: Submenu panel not found:', submenuId);
                    return;
                }

                if (submenuPanel.classList.contains('is-active')) return;

                this.closeAllPanels(submenuId);

                this.mainPanel?.classList.add('has-active-submenu');
                submenuPanel.classList.add('is-active');
                submenuPanel.setAttribute('aria-hidden', 'false');
                trigger.setAttribute('aria-expanded', 'true');

                this.focusFirstElement(submenuPanel);
            } catch (error) {
                console.error('TD Navigation: Error opening submenu', error);
            }
        }

        openGrandchildPanel(trigger) {
            try {
                const grandchildId = trigger.getAttribute('data-grandchild-id');
                if (!grandchildId) return;

                const grandchildPanel = document.getElementById(grandchildId);
                if (!grandchildPanel) {
                    console.warn('TD Navigation: Grandchild panel not found:', grandchildId);
                    return;
                }

                if (grandchildPanel.classList.contains('is-active')) return;

                const otherGrandchildPanels = document.querySelectorAll(`${this.SELECTORS.grandchildPanels}.is-active`);
                otherGrandchildPanels.forEach(panel => {
                    if (panel.id !== grandchildId) this.closePanel(panel);
                });

                const parentSubmenu = trigger.closest('.td-mobile-menu__submenu-panel');
                if (parentSubmenu) parentSubmenu.classList.add('has-active-grandchild');

                grandchildPanel.classList.add('is-active');
                grandchildPanel.setAttribute('aria-hidden', 'false');
                trigger.setAttribute('aria-expanded', 'true');

                this.focusFirstElement(grandchildPanel);
            } catch (error) {
                console.error('TD Navigation: Error opening grandchild panel', error);
            }
        }

        handleBackButton(button) {
            try {
                const currentPanel = button.closest('.td-mobile-menu__panel');
                if (!currentPanel) return;

                const backTo = button.getAttribute('data-back-to');
                const isGoingToMain = backTo === 'main';
                const parentPanel = !isGoingToMain ? document.getElementById(backTo) : null;

                button.blur();

                const trigger = this.closePanel(currentPanel);

                if (isGoingToMain) {
                    this.mainPanel?.classList.remove('has-active-submenu');
                }

                if (trigger) {
                    setTimeout(() => trigger.focus(), this.FOCUS_DELAY);
                } else if (isGoingToMain && this.mainPanel) {
                    setTimeout(() => this.focusFirstElement(this.mainPanel), this.FOCUS_DELAY);
                } else if (!isGoingToMain && parentPanel) {
                    setTimeout(() => this.focusFirstElement(parentPanel), this.FOCUS_DELAY);
                }
            } catch (error) {
                console.error('TD Navigation: Error handling back button', error);
            }
        }

        toggleBodyOverflow(hide) {
            if (hide) {
                document.body.classList.add('td-hide-overflow');
            } else {
                document.body.classList.remove('td-hide-overflow');
            }
        }
    }

    customElements.define("td-mobile-navigation", TDMobileNavigation);
}
